// src/services/apaleo/units.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import i18n from "../../i18n";
import type { UnitModel, UnitGroupModel } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData, ALLOWED_PROPERTIES } from './config';

const part: ThisType<ApaleoClient> = {
  async fetchUnits(propertyId: string): Promise<UnitModel[]> {
    if (IS_DEV)
      return MockData.MOCK_UNITS[propertyId] || MockData.MOCK_UNITS.BERLIN;
    if (!ALLOWED_PROPERTIES.includes(propertyId))
      throw new Error(`${i18n.t('common.error')}: ${propertyId}`);
    try {
      let allUnits: UnitModel[] = [];
      let pageNumber = 1;
      let hasMore = true;
      let safetyCounter = 0;

      while (hasMore && safetyCounter < 10) {
        const data = (await this._fetch(
          `/inventory/v1/units?propertyId=${propertyId}&pageNumber=${pageNumber}&pageSize=200`,
        )) as { units?: UnitModel[] } | null;
        if (data && data.units) {
          allUnits = [...allUnits, ...data.units];
          hasMore = data.units.length === 200;
          pageNumber++;
        } else {
          hasMore = false;
        }
        safetyCounter++;
      }
      return allUnits;
    } catch (error) {
      console.error(
        `Fehler beim Laden der Einheiten für ${propertyId}:`,
        error,
      );
      return [];
    }
  },

  async fetchUnitsByGroup(propertyId: string, unitGroupId: string): Promise<UnitModel[]> {
    if (IS_DEV)
      return MockData.MOCK_UNITS[propertyId] || MockData.MOCK_UNITS.BERLIN;
    if (!ALLOWED_PROPERTIES.includes(propertyId))
      throw new Error(`${i18n.t('common.error')}: ${propertyId}`);
    try {
      let allUnits: UnitModel[] = [];
      let pageNumber = 1;
      let hasMore = true;
      let safetyCounter = 0;

      while (hasMore && safetyCounter < 10) {
        const data = (await this._fetch(
          `/inventory/v1/units?propertyId=${propertyId}&unitGroupId=${unitGroupId}&pageNumber=${pageNumber}&pageSize=200`,
        )) as { units?: UnitModel[] } | null;
        if (data && data.units) {
          allUnits = [...allUnits, ...data.units];
          hasMore = data.units.length === 200;
          pageNumber++;
        } else {
          hasMore = false;
        }
        safetyCounter++;
      }
      return allUnits;
    } catch (error) {
      console.error(
        `Fehler beim Laden der Einheiten für Gruppe ${unitGroupId}:`,
        error,
      );
      return [];
    }
  },

  async updateUnitConditions(
    propertyId: string,
    unitsConditions: { id: string; condition: string }[],
  ): Promise<unknown> {
    if (!ALLOWED_PROPERTIES.includes(propertyId))
      throw new Error(`${i18n.t('common.error')}: ${propertyId}`);
    return await this._fetch(`/operations/v1/units-condition`, {
      method: "PUT",
      body: JSON.stringify({ unitsConditions }),
    });
  },

  async setRoomStatus(
    unitId: string,
    isClean: boolean,
    propertyId: string,
  ): Promise<unknown> {
    return this.updateUnitConditions(propertyId, [
      { id: unitId, condition: isClean ? "Clean" : "Dirty" },
    ]);
  },

async fetchUnitCounts() {
  if (IS_DEV)
    return {
      total: 10,
      perProperty: { BERLIN: 5, GIESSEN: 2, SIEGEN: 2, WETZLAR: 1 },
    };
  try {
    if (ALLOWED_PROPERTIES.length === 0) return { total: 0, perProperty: {} };

    const promises = ALLOWED_PROPERTIES.map((id) =>
      this._fetch(`/inventory/v1/units?propertyId=${id}&limit=1`)
        .then((data) => ({
          count: Number((data as { count?: number } | null)?.count || 0),
          id,
          success: true,
        }))
        .catch(() => ({ count: 0, id, success: false })),
    );

    const results = await Promise.all(promises);
    const countMap: Record<string, number> = {};
    let totalUnits = 0;
    results.forEach((r) => {
      if (r.success) {
        countMap[r.id] = r.count;
        totalUnits += r.count;
      }
    });
    return { total: totalUnits, perProperty: countMap };
  } catch {
    return { total: 0, perProperty: {} };
  }
},

async fetchUnitGroups(propertyId: string): Promise<UnitGroupModel[]> {
  if (IS_DEV) return MockData.MOCK_UNIT_GROUPS;
  try {
    if (!ALLOWED_PROPERTIES.includes(propertyId))
      throw new Error(`Sicherheitssperre für Property ${propertyId}`);
    const data = (await this._fetch(
      `/inventory/v1/unit-groups?propertyId=${propertyId}&unitGroupTypes=Bedroom`,
    )) as { unitGroups?: UnitGroupModel[] } | null;
    return data?.unitGroups || [];
  } catch (error) {
    console.error("Fehler beim Laden der Kategorien:", error);
    throw error;
  }
},

async fetchAvailability(propertyId: string, from: string, to: string): Promise<unknown> {
  if (IS_DEV) {
    console.log(
      `ApaleoAuth (Mock): fetchAvailability for Property: ${propertyId}, from: ${from}, to: ${to}`,
    );
    const timeSlices: unknown[] = [];
    const startDate = new Date(from);
    const endDate = new Date(to);
    let count = 0;

    for (
      let d = new Date(startDate);
      d <= endDate && count < 50;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = this._localDateIso(d);
      timeSlices.push({
        from: `${dateStr}T15:00:00Z`,
        to: `${this._localDateIso(new Date(d.getTime() + 86400000))}T11:00:00Z`,
        property: {
          physicalCount: 30,
          houseCount: 26,
          soldCount: 13,
          occupancy: 50.0,
          sellableCount: 9,
          allowedOverbookingCount: 1,
          houseOverbookingLimit: 2,
          maintenance: { outOfService: 2, outOfOrder: 4, outOfInventory: 4 },
          block: { definite: 1, tentative: 1, picked: 3, remaining: 1 },
        },
        unitGroups: [
          {
            unitGroup: {
              id: "UG-DBL",
              code: "DBL",
              name: "Double Room",
              description: "Double Room",
              type: "BedRoom",
            },
            physicalCount: 20,
            houseCount: 18,
            soldCount: 10,
            occupancy: 55.55,
            availableCount: 5,
            sellableCount: 5,
            allowedOverbookingCount: 1,
            maintenance: {
              outOfService: 1,
              outOfOrder: 2,
              outOfInventory: 2,
            },
            block: { definite: 6, tentative: 1, picked: 3, remaining: 2 },
          },
          {
            unitGroup: {
              id: "UG-SNG",
              code: "SNG",
              name: "Single Room",
              description: "Single Room",
              type: "BedRoom",
            },
            physicalCount: 10,
            houseCount: 8,
            soldCount: 3,
            occupancy: 37.5,
            availableCount: 5,
            sellableCount: 5,
            allowedOverbookingCount: 0,
            maintenance: {
              outOfService: 1,
              outOfOrder: 2,
              outOfInventory: 2,
            },
            block: { definite: 2, tentative: 5, picked: 1, remaining: 2 },
          },
        ],
      });
      count++;
    }
    return {
      count: timeSlices.length,
      timeSlices,
    };
  }

  try {
    if (!ALLOWED_PROPERTIES.includes(propertyId))
      throw new Error(`${i18n.t('common.error')}: ${propertyId}`);
    return await this._fetch(
      `/availability/v1/unit-groups?propertyId=${propertyId}&from=${from}&to=${to}&unitGroupTypes=Bedroom`,
    );
  } catch (error) {
    console.error(
      `Fehler beim Laden der Verfügbarkeit für Property ${propertyId}:`,
      error,
    );
    throw error;
  }
},

};

export default part;
